import { InformationCircleIcon } from '@heroicons/react/24/solid'
import Link from 'next/link'
import { ChangeEvent, ClipboardEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { PaymentCard, PaySystem } from '@/api/models/finance'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import CardsGroup from '@/components/shared/cards-group/ui/CardsGroup'
import { AppInput } from '@/components/shared/input'
import AppInputMaskedLocalized from '@/components/shared/input/ui/AppInputMaskedLocalized'
import { ButtonSkeleton, CardSkeleton } from '@/components/shared/skeleton'

import { wrongNumberFormat } from '../../../../model/constants'
import { PaymentFormData } from './model/schemas'

type Props = {
  isPrepayment: boolean
  urlOffer: string
  urlDesc: string
  isLoading: boolean
  cards: PaymentCard[]
  selectedCard?: PaymentCard
  onCardSelect: (selectedCard: PaymentCard | undefined) => void
  showEmailBlock?: boolean
}

export const PaymentForm = ({
  isPrepayment,
  urlDesc,
  urlOffer,
  isLoading,
  cards,
  selectedCard,
  onCardSelect,
  showEmailBlock = true
}: Props) => {
  const {
    control,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<PaymentFormData>()
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8 px-40 lg:px-24 sm:px-0">
        <ButtonSkeleton className="h-[28px] w-[367px] xxs:h-[20px] xxs:w-[269px]" />
        <CardSkeleton className="mb-12 h-56 w-full" />
        <ButtonSkeleton className="h-[16px] w-full" />
        <ButtonSkeleton className="h-[16px] w-[247px] xxs:w-[318px]" />
        {isMobileScreen && (
          <>
            <ButtonSkeleton className="h-[16px] w-full" />
            <ButtonSkeleton className="h-[16px] w-[247px]" />
          </>
        )}
      </div>
    )
  }

  const cardsQuantity =
    cards.filter(
      card =>
        card.paySystem !== PaySystem.Acquiring &&
        card.paySystem !== PaySystem.Qr
    ).length || 0

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text')
    if (!/\d/.test(text)) {
      setError('amount', { type: 'manual', message: wrongNumberFormat })
    }
  }

  return (
    <form className="mt-32 flex flex-col gap-32 sm:mt-20">
      <div className={cards.length === 1 ? 'mr-40 lg:mr-0' : ''}>
        <p className="mb-16 ml-40 font-pro font-bold text-24 lg:ml-24 md:text-20 sm:mx-0">
          Способ оплаты
        </p>

        <CardsGroup
          cardsQuantity={cardsQuantity}
          options={cards}
          selectedCard={selectedCard}
          onCardSelect={onCardSelect}
        />
      </div>
      {isPrepayment && (
        <div className="mx-40 lg:mx-24 sm:mx-0">
          <p className="font-bold text-20">Сумма платежа</p>
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <AppInputMaskedLocalized
                dataTestId="payment-amount-input-id"
                value={
                  field.value && Number(field.value) < 0
                    ? ''
                    : String(field.value)
                }
                placeholder="Введите сумму"
                className="mb-4 mt-8 h-56 flex-auto border border-grey-20 text-16"
                onChange={(event: ChangeEvent) => {
                  clearErrors('amount')
                  field.onChange(event)
                }}
                onPaste={handlePaste}
                invalid={Boolean(errors.amount?.message)}
                invalidMessage={errors.amount?.message}
              />
            )}
          />
          {!errors.amount?.message && (
            <div className="flex gap-4 text-grey-70">
              <InformationCircleIcon className="h-20 w-20" /> Не более 1 000 000
              ₽
            </div>
          )}
        </div>
      )}

      {showEmailBlock && (
        <div className="mx-40 lg:mx-24 sm:mx-0">
          <p className="font-bold text-20">
            Email для отправки фискального чека
          </p>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <AppInput
                dataTestId="payment-email-input-id"
                value={field.value}
                placeholder="Email"
                className="mb-16 mt-8 h-56 flex-auto border border-grey-20 text-16"
                onChange={field.onChange}
                invalid={Boolean(errors.email?.message)}
                invalidMessage={errors.email?.message}
              />
            )}
          />
          <p>
            Оплатить счёт можно только банковской картой. Нажимая кнопку
            «Оплатить банковской картой», вы принимаете условия{' '}
            <Link href={urlOffer} target="_blank" className="text-bronze-40">
              Публичной оферты
            </Link>{' '}
            и{' '}
            <Link href={urlDesc} target="_blank" className="text-bronze-40">
              Информации об оплате картами
            </Link>
          </p>
        </div>
      )}
    </form>
  )
}
